import {
  Box,
  useTheme,
  Typography,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { useStateContext } from "contexts/ContextProvider";
import React, { useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import { useWebSocket } from "contexts/WebSocketContext";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useAuth } from "../../../contexts/AuthContext";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { usePageTitle } from "hooks/usePageTitle";
import { formatAmount } from "helpers/ServiceHelpers";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const InvoiceEdit = () => {
  const { currentUser } = useAuth();

  const theme = useTheme();

  // Convert duration string (HH:MM:SS) to decimal hours with proper formatting
  const convertDurationToDecimal = (durationString) => {
    if (!durationString) return "0 hours";

    const parts = durationString.split(":");
    if (parts.length === 3) {
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      const seconds = parseInt(parts[2]) || 0;
      const decimalHours = hours + minutes / 60 + seconds / 3600;

      // Round to 2 decimals first
      const rounded = Math.round(decimalHours * 100) / 100;

      // Format: show as integer if no decimal part, otherwise with 2 decimals
      let formattedHours;
      if (rounded % 1 === 0) {
        formattedHours = Math.floor(rounded);
      } else {
        formattedHours = rounded.toFixed(2);
      }

      // Return with proper singular/plural
      const unit = parseFloat(formattedHours) === 1 ? "hour" : "hours";
      return `${formattedHours} ${unit}`;
    }
    return durationString;
  };

  const {
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    openDialog,
    handleErrorDialog,
    closeDialog,
  } = useStateContext();

  const { socket } = useWebSocket();

  usePageTitle("Edit Invoice");

  // Edit lock for existing invoices only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "invoice",
    overlayData?.invoiceData?.id || null,
  );

  // Get invoice data from overlayData
  const invoiceData = overlayData?.invoiceData || {};

  const invoiceNumber = invoiceData.Number || "";
  const [invoiceDate, setInvoiceDate] = useState(
    invoiceData.Date ? dayjs(invoiceData.Date) : dayjs(),
  );
  const [invoiceDueDate, setInvoiceDueDate] = useState(
    invoiceData.DueDate ? dayjs(invoiceData.DueDate) : dayjs().add(30, "day"),
  );
  const [invoiceNote, setInvoiceNote] = useState(invoiceData.Note || "");
  const [purchaseOrder, setPurchaseOrder] = useState(
    invoiceData.PurchaseOrder || "",
  );
  const [clientName, setClientName] = useState(
    invoiceData.ClientName || invoiceData.Client?.Account || "",
  );
  const [address, setAddress] = useState(
    invoiceData.Address || invoiceData.Client?.MainAddress || "",
  );
  const [paymentLink, setPaymentLink] = useState(invoiceData.PaymentLink || "");
  const invoiceClient = invoiceData.Client || null;
  const [invoiceServices, setInvoiceServices] = useState(
    (invoiceData.Services || []).map((service) => ({
      ...service,
      OriginalDetails: service.Details,
      OriginalServiceClientRate: service.ServiceClientRate,
    })),
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);

  dayjs.extend(durationPlugin);

  const updateServiceInvoice = async (service, invoiceData) => {
    try {
      // Call the new simplified invoice update endpoint
      const response = await api.put(`/services/${service.id}/invoice`, {
        InvoiceID: invoiceData.id,
        Details: service.Details,
        ServiceClientRate:
          service.ServiceClientRate !== undefined
            ? service.ServiceClientRate
            : null, // Include custom rate if set
      });

      return response.data;
    } catch (error) {
      console.error("Error updating service invoice:", error);
      throw error;
    }
  };

  const removeServiceFromInvoice = async (service) => {
    try {
      // Call endpoint to clear the invoice ID
      const response = await api.put(`/services/${service.id}/invoice/remove`);
      return response.data;
    } catch (error) {
      console.error("Error removing service from invoice:", error);
      throw error;
    }
  };

  // Edit details state
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editDetailsText, setEditDetailsText] = useState("");
  const [editRateText, setEditRateText] = useState("");

  // Add service dialog state
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedNewServices, setSelectedNewServices] = useState([]);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [deletedServices, setDeletedServices] = useState([]);

  // Keep Due Date in sync: always 30 days after invoiceDate
  useEffect(() => {
    if (!invoiceDate) return;
    setInvoiceDueDate(invoiceDate.add(30, "day"));
  }, [invoiceDate]);


  // Fetch available services for adding to invoice
  const fetchAvailableServices = async () => {
    if (!invoiceClient) return;

    try {
      const response = await api.get("/services/invoices", {
        params: {
          clientId: invoiceClient.id,
          invoiced: "false", // Only non-invoiced services
        },
      });

      // Store all services without filtering - we'll filter dynamically in the dialog
      setAvailableServices(response.data);
    } catch (error) {
      console.error("Error fetching available services:", error);
      setAvailableServices([]);
    }
  };

  // Open dialog to add services to invoice
  const openAddServiceDialog = () => {
    setIsServiceDialogOpen(true);
  };

  const handleAddSelectedServices = useCallback(() => {
    // Combine availableServices and deletedServices for lookup
    const allServices = [...availableServices, ...deletedServices];

    const newItems = selectedNewServices.map((serviceId) => {
      const service = allServices.find((s) => s.id === serviceId);
      return {
        id: service.id,
        ProjectNumber: service.ProjectNumber,
        Date: service.Date,
        Details: service.Details,
        OriginalDetails: service.Details,
        OriginalServiceClientRate: service.ServiceClientRate,
        ServiceUnit: service.ServiceUnit,
        ServiceClientRate: service.ServiceClientRate,
        AccountCharge: service.AccountCharge || 0,
        isNewlyAdded: true,
        ...service,
      };
    });

    setInvoiceServices((prev) => [...prev, ...newItems]);
    setDeletedServices((prev) =>
      prev.filter((s) => !selectedNewServices.includes(s.id)),
    );
    closeDialog();
    setSelectedNewServices([]);
  }, [availableServices, deletedServices, selectedNewServices, closeDialog]);

  // Effect to update dialog when selection changes
  useEffect(() => {
    if (!isServiceDialogOpen) return;

    const combinedServices = [...availableServices, ...deletedServices];

    // Remove duplicates by id
    const uniqueServices = combinedServices.filter(
      (service, index, self) =>
        index === self.findIndex((s) => s.id === service.id),
    );

    // Filter out services already in the invoice
    const existingServiceIds = new Set(
      (invoiceServices || []).map((item) => item.id),
    );
    const filteredServices = uniqueServices.filter(
      (service) => !existingServiceIds.has(service.id),
    );

    const content =
      filteredServices.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          No available services found for this client. All completed services
          may already be invoiced.
        </Typography>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select services to add to this invoice ({filteredServices.length}{" "}
            available)
          </Typography>

          {/* Service List */}
          <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
            {(() => {
              // Group services by month/year
              const grouped = filteredServices.reduce((acc, service) => {
                if (!service.Date) return acc;
                const monthYear = dayjs(service.Date).format("MMMM YYYY");
                if (!acc[monthYear]) {
                  acc[monthYear] = [];
                }
                acc[monthYear].push(service);
                return acc;
              }, {});

              // Sort by date descending (most recent first)
              const sortedMonths = Object.keys(grouped).sort((a, b) => {
                return (
                  dayjs(b, "MMMM YYYY").valueOf() -
                  dayjs(a, "MMMM YYYY").valueOf()
                );
              });

              return sortedMonths.map((monthYear) => (
                <Box
                  key={monthYear}
                  sx={{
                    mb: 2,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    pb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {monthYear}
                  </Typography>
                  {grouped[monthYear].map((service) => (
                    <Box
                      key={service.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        mb: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        bgcolor: selectedNewServices.includes(service.id)
                          ? theme.palette.action.selected
                          : "transparent",
                        "&:hover": {
                          bgcolor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        setSelectedNewServices((prev) =>
                          prev.includes(service.id)
                            ? prev.filter((id) => id !== service.id)
                            : [...prev, service.id],
                        );
                      }}
                    >
                      <Checkbox
                        checked={selectedNewServices.includes(service.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          setSelectedNewServices((prev) =>
                            prev.includes(service.id)
                              ? prev.filter((id) => id !== service.id)
                              : [...prev, service.id],
                          );
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={500}>
                          Project #: {service.ProjectNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.Details || "No details"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {service.Date
                            ? dayjs(service.Date).format("MM/DD/YYYY")
                            : "No date"}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={500}>
                        $
                        {typeof service.AccountCharge === "number"
                          ? service.AccountCharge.toFixed(2)
                          : (parseFloat(service.AccountCharge) || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ));
            })()}
          </Box>
        </Box>
      );

    openDialog({
      theme: theme,
      title: `Add Services to Invoice - Client: ${
        invoiceClient?.Account || "N/A"
      }`,
      message: null,
      customContent: content,
      secondaryAction: "Cancel",
      primaryAction:
        filteredServices.length > 0 && selectedNewServices.length > 0
          ? `Add (${selectedNewServices.length}) Selected`
          : null,
      secondaryOnClick: () => {
        closeDialog();
        setIsServiceDialogOpen(false);
        setSelectedNewServices([]);
      },
      primaryOnClick:
        filteredServices.length > 0 && selectedNewServices.length > 0
          ? () => {
              handleAddSelectedServices();
              setIsServiceDialogOpen(false);
            }
          : null,
    });
  }, [
    isServiceDialogOpen,
    selectedNewServices,
    availableServices,
    invoiceServices,
    deletedServices,
    closeDialog,
    handleAddSelectedServices,
    invoiceClient?.Account,
    openDialog,
    theme,
  ]);

  const saveInvoices = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (!invoiceServices || invoiceServices.length === 0) {
      return;
    }

    setIsSavingChanges(true);

    try {
      if (invoiceData.id) {
        // UPDATE existing invoice
        const updatedInvoiceData = {
          Date: invoiceDate.format("YYYY-MM-DD"),
          DueDate: invoiceDueDate.format("YYYY-MM-DD"),
          ClientName: clientName || null,
          Address: address || null,
          Note: invoiceNote || null,
          PurchaseOrder: purchaseOrder || null,
          PaymentLink: paymentLink || null,
          Amount: invoiceServices.reduce(
            (sum, service) => sum + (parseFloat(service.AccountCharge) || 0),
            0,
          ),
        };

        await api.put(`/invoices/${invoiceData.id}`, updatedInvoiceData);

        // Update services linked to this invoice - handle both modified and newly added services
        if (invoiceServices.length > 0) {
          const servicesToUpdate = invoiceServices.filter((service) => {
            // Include newly added services
            if (service.isNewlyAdded) {
              return true;
            }

            // Check if Details changed
            const detailsChanged = service.Details !== service.OriginalDetails;
            // Check if ServiceClientRate changed (comparing with original or checking if it was set)
            const rateChanged =
              service.ServiceClientRate !== undefined &&
              service.ServiceClientRate !== service.OriginalServiceClientRate;

            return detailsChanged || rateChanged;
          });

          if (servicesToUpdate.length > 0) {
            await Promise.all(
              servicesToUpdate.map((service) =>
                updateServiceInvoice(service, invoiceData),
              ),
            );
          }
        }

        // Remove deleted services from invoice (set InvoiceID to null)
        if (deletedServices.length > 0) {
          await Promise.all(
            deletedServices.map((service) => removeServiceFromInvoice(service)),
          );
        }

        // Emit socket events for real-time updates
        if (socket) {
          // Notify profile viewers of this invoice
          socket.emit("entity:saved", {
            modelType: "Invoice",
            recordId: invoiceData.id,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
          // Notify list viewers
          socket.emit("entity:saved", {
            modelType: "Invoice",
            recordId: "all",
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("Invoice Updated Successfully!", theme);
        setOverlayName("");
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save invoice. Please try again.";
      handleErrorDialog(message, theme);
    } finally {
      setIsSavingChanges(false);
    }
  };

  return (
    <Box
      bgcolor="#fff"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: "80%",
          height: "85%",
          minWidth: "600px",
          maxWidth: "1400px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "column",
            p: ".75rem 2rem",
            gap: 2,
          }}
        >
          {/* Header row with title and menu */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                color="primary"
                size="large"
                onClick={() => setOverlayName("")}
              >
                <ChevronLeftRoundedIcon fontSize="inherit" />
              </IconButton>

              <Typography variant="h2" fontWeight="bold">
                Edit Invoice #{invoiceNumber}
              </Typography>
            </Box>
          </Box>

          {/* Invoice Edit Fields */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
            {/* Row 1: Name, Date, Due Date */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Client Name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label="Date"
                value={invoiceDate}
                onChange={(newValue) => setInvoiceDate(newValue)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Due Date (auto 30 days)"
                value={invoiceDueDate.format("MM/DD/YYYY")}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>

            {/* Row 2: Address, Note, PO */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Invoice Note"
                value={invoiceNote}
                onChange={(e) => setInvoiceNote(e.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Purchase Order"
                value={purchaseOrder}
                onChange={(e) => setPurchaseOrder(e.target.value)}
                multiline
                rows={2}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Payment Link"
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>

        {/* Invoice Items Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: "1rem 2rem",
            width: "100%",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mb: 1.5,
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              Projects
            </Typography>
            <Tooltip title="Add Service">
              <IconButton
                color="primary"
                size="large"
                onClick={async () => {
                  await fetchAvailableServices();
                  openAddServiceDialog();
                }}
              >
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Header Row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 6fr 1fr 1fr 1fr 80px",
              gap: 1,
              bgcolor: theme.palette.background.default,
              borderBottom: `2px solid ${theme.palette.divider}`,
              width: "100%",
              fontWeight: 700,
            }}
          >
            <Typography variant="h6" color={theme.palette.primary.main}>
              Project
            </Typography>
            <Typography variant="h6" color={theme.palette.primary.main}>
              Date
            </Typography>
            <Typography variant="h6" color={theme.palette.primary.main}>
              Details
            </Typography>
            <Typography variant="h6" color={theme.palette.primary.main}>
              Unit
            </Typography>
            <Typography variant="h6" color={theme.palette.primary.main}>
              Rate
            </Typography>
            <Typography variant="h6" color={theme.palette.primary.main}>
              Total
            </Typography>
            <Typography variant="h6" color={theme.palette.primary.main}>
              Actions
            </Typography>
          </Box>

          {/* Items List */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {!invoiceServices || invoiceServices.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  No services added to this invoice yet.
                </Typography>
              </Box>
            ) : (
              invoiceServices.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 6fr 1fr 1fr 1fr 80px",
                    gap: 1,
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2">{item.ProjectNumber}</Typography>
                  <Typography variant="body2">
                    {item.Date ? dayjs(item.Date).format("MM-DD-YYYY") : "N/A"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {editingItemIndex === index ? (
                      <TextField
                        value={editDetailsText}
                        onChange={(e) => setEditDetailsText(e.target.value)}
                        size="small"
                        multiline
                        fullWidth
                        autoFocus
                      />
                    ) : (
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.Details || "N/A"}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">
                    {item.ServiceCategory === "Interpretation"
                      ? convertDurationToDecimal(item.TotalDuration)
                      : item.WordCount && item.WordCount > 0
                        ? `${item.WordCount} words`
                        : "N/A"}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {editingItemIndex === index ? (
                      <TextField
                        value={editRateText}
                        onChange={(e) => setEditRateText(e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="0.00"
                        inputProps={{ style: { textAlign: "right" } }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, textAlign: "right" }}
                      >
                        {(() => {
                          if (item.ServiceCategory === "Interpretation") {
                            const rate =
                              item.ServiceClientRate !== null &&
                              item.ServiceClientRate !== undefined
                                ? item.ServiceClientRate
                                : item.Client?.ClientRates?.[0]?.Rate;
                            if (rate === 0) return "N/A";
                            return rate !== null && rate !== undefined
                              ? formatAmount(rate)
                              : "N/A";
                          } else {
                            const rate = item.Rate;
                            if (rate === 0) return "N/A";
                            return rate !== null && rate !== undefined
                              ? formatAmount(rate)
                              : "N/A";
                          }
                        })()}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2">
                    $
                    {typeof item.AccountCharge === "number"
                      ? item.AccountCharge.toFixed(2)
                      : (parseFloat(item.AccountCharge) || 0).toFixed(2)}
                  </Typography>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}
                  >
                    <IconButton
                      size="small"
                      sx={{ width: 36, height: 36, borderRadius: "50%" }}
                      onClick={() => {
                        if (editingItemIndex === index) {
                          // Save changes
                          setInvoiceServices((prev) => {
                            const updated = [...prev];
                            updated[index].Details = editDetailsText;
                            // Update ServiceClientRate if changed
                            const trimmedRate = editRateText.trim();
                            if (
                              trimmedRate === "" ||
                              trimmedRate.toLowerCase() === "n/a"
                            ) {
                              // Clear custom rate (set to null)
                              updated[index].ServiceClientRate = null;
                            } else {
                              const newRate = parseFloat(
                                trimmedRate.replace(/[^0-9.-]/g, ""),
                              );
                              if (!isNaN(newRate)) {
                                // Allow 0 as a valid rate
                                updated[index].ServiceClientRate = newRate;
                              }
                            }
                            return updated;
                          });
                          setEditingItemIndex(null);
                          setEditDetailsText("");
                          setEditRateText("");
                        } else {
                          // Start editing
                          setEditingItemIndex(index);
                          setEditDetailsText(item.Details || "");
                          // Set current rate for editing
                          const currentRate =
                            item.ServiceCategory === "Interpretation"
                              ? item.ServiceClientRate !== null &&
                                item.ServiceClientRate !== undefined
                                ? item.ServiceClientRate
                                : item.Client?.ClientRates?.[0]?.Rate
                              : item.Rate;
                          setEditRateText(
                            currentRate !== null && currentRate !== undefined
                              ? currentRate.toString()
                              : "N/A",
                          );
                        }
                      }}
                    >
                      {editingItemIndex === index ? (
                        <CheckRoundedIcon fontSize="small" />
                      ) : (
                        <EditRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      sx={{ width: 36, height: 36, borderRadius: "50%" }}
                      onClick={() => {
                        const deletedService = invoiceServices[index];
                        setDeletedServices((prev) => [...prev, deletedService]);
                        setInvoiceServices((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                      }}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" color={theme.palette.primary.main}>
            Total: $
            {(invoiceServices || [])
              .reduce(
                (sum, item) => sum + (parseFloat(item.AccountCharge) || 0),
                0,
              )
              .toFixed(2)}
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              color="primary"
              variant="outlined"
              size="large"
              endIcon={<DisabledByDefaultRoundedIcon />}
              onClick={() => setOverlayName("")}
              sx={{ borderRadius: "12px" }}
            >
              Discard
            </Button>

            <Button
              disabled={
                isSavingChanges ||
                !invoiceServices ||
                invoiceServices.length === 0
              }
              color="primary"
              variant="contained"
              size="large"
              endIcon={<SaveRoundedIcon />}
              onClick={saveInvoices}
              sx={{ borderRadius: "12px" }}
            >
              {isSavingChanges ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default InvoiceEdit;
